import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, Subscription, tap } from 'rxjs';
import { SupplierClass } from 'settings/interfaces/supplier-class';
import { Language } from 'settings/interfaces/language.interface';
import { ID_ZERO } from '../constants/general-constants';
import { CachingService } from './caching.service';

const GLOBAL_CONFIG_API = environment.BASE_URL + 'globalConfigApi/';

const urls = {
    languages: 'languages',
    supplierClasses: 'supplierClasses',
    listingTypes: 'listingTypes',
};

@Injectable({
    providedIn: 'root',
})
export class GlobalConfigService {
    public supplierClasses?: SupplierClass[];
    public languages?: Language[];
    private subscriptions: { [key: string]: Subscription } = {};

    constructor(
        private http: HttpClient,
        private cachingService: CachingService,
    ) {}

    private static getLanguagesURL(): string {
        return GLOBAL_CONFIG_API + urls.languages;
    }

    private static getSupplierClassesURL(): string {
        return GLOBAL_CONFIG_API + urls.supplierClasses;
    }

    // Get languages
    public getLanguages(): Observable<Language[]> {
        return this.cachingService.wrapCache('languages', () => {
            return this.http.get<Language[]>(GlobalConfigService.getLanguagesURL()).pipe(
                catchError(() => {
                    return of([
                        {
                            id: null,
                            name: null,
                        },
                    ]) as Observable<Language[]>;
                }),
            );
        }) as Observable<Language[]>;
    }

    // Get supplier classes
    public getSupplierClasses(): Observable<SupplierClass[]> {
        return this.http.get<SupplierClass[]>(GlobalConfigService.getSupplierClassesURL()).pipe(
            tap((classes) => (this.supplierClasses = classes)),
            catchError(() => {
                return of([
                    {
                        id: null,
                        name: null,
                    },
                ]) as Observable<SupplierClass[]>;
            }),
        );
    }

    public removeLanguages() {
        this.languages = undefined;
    }

    public removeSupplierClasses() {
        this.supplierClasses = undefined;
    }

    public getSupplierClassNameFromId(supplierClassId: string): string {
        return this.supplierClasses?.find((sClass) => sClass.id === supplierClassId)?.name || '';
    }

    public getSupplierClassIdsFromNames(supplierClassNames: string | string[]): (string | null)[] | undefined {
        if (this.supplierClasses === undefined) {
            this.subscriptions.getSupplierClasses = this.getSupplierClasses().subscribe((data) => (this.supplierClasses = data));
        }

        if (supplierClassNames === undefined || supplierClassNames?.length === 0) {
            return this.supplierClasses?.map((sClass) => sClass.id);
        }

        if (!Array.isArray(supplierClassNames)) {
            supplierClassNames = [supplierClassNames];
        }

        supplierClassNames = supplierClassNames.map((name) => name.toUpperCase());

        const filteredSupplierClasses = this.supplierClasses
            ?.filter((supplierClass) => supplierClassNames.includes(supplierClass?.name?.toUpperCase() ?? ''))
            ?.map((sClass) => sClass.id);
        if (filteredSupplierClasses?.length === 0) {
            return [ID_ZERO];
        } else {
            return filteredSupplierClasses;
        }
    }
}
